import React, { Component, Fragment, createRef } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import DecimationSlider from './DecimationSlider';
import { circleX, settingsIcon, approveIcon, deleteIcon } from '../../Templates/icons';
import Dialogue from '../../Templates/Dialogue';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import ModelControls from '../../Templates/ModelControls';

class DecimationReviewForm extends Component {
    constructor(props) {
        super(props);
        this.containerRef = createRef();
        this.currentRightObject = null;
        this.preloadedRightModels = {};
        this.leftModelLoaded = false;
        this.rightModelLoaded = false;
        this.state = {
            webGLLoading: true,
            authSent: false,
            loadedAssetBundles: false,
            backgroundColor: "#fff",
            modelsLoaded: false,
            displayWebGLSettings: false,
            brightness: true,
            decimationSetting: 2,
            progression: 0,
            hasError: false,
            width: 600,
            height: 600,
            decimationSetting: 2,
            metrics: {}
        };
    }
    getObjectStats = (object) => {
        let verticesCount = 0;
        let trianglesCount = 0;
        let partsCount = 0;
        
        // console.log("Analyzing model geometry...");
        
        object.traverse(child => {
            if (child.isMesh && child.geometry) {
                partsCount++;
                const geometry = child.geometry;
                
                // Debug info
                // console.log("Part geometry:", {
                //     isIndexed: !!geometry.index,
                //     positionCount: geometry.attributes.position.count,
                //     positionArray: geometry.attributes.position.array.length,
                //     indexCount: geometry.index ? geometry.index.count : "N/A"
                // });
                
                if (geometry.isBufferGeometry && geometry.attributes.position) {
                    // Count triangles
                    if (geometry.index) {
                        trianglesCount += geometry.index.count / 3;
                        
                        // For large models, use a sampling approach to estimate unique vertices
                        const indexArray = geometry.index.array;
                        const totalIndices = indexArray.length;
                        
                        if (totalIndices > 1000000) { // If more than 1M indices, use sampling
                            // Sample every nth index to estimate unique vertices
                            const sampleRate = Math.max(1, Math.floor(totalIndices / 100000)); // Sample ~100k indices
                            const sampleSet = new Set();
                            
                            for (let i = 0; i < totalIndices; i += sampleRate) {
                                sampleSet.add(indexArray[i]);
                            }
                            
                            // Estimate total unique vertices based on sample
                            const uniqueInSample = sampleSet.size;
                            const estimatedUnique = Math.round(uniqueInSample * sampleRate);
                            verticesCount += Math.min(estimatedUnique, geometry.attributes.position.count);
                            
                            // console.log("Large indexed geometry - estimated unique vertices:", estimatedUnique);
                        } else {
                            // For smaller models, use the original approach
                            try {
                                const uniqueIndices = new Set(Array.from(indexArray));
                                verticesCount += uniqueIndices.size;
                                // console.log("Indexed geometry - unique vertices:", uniqueIndices.size);
                            } catch (e) {
                                // Fallback if Set still fails
                                console.warn("Set creation failed, using position count as estimate");
                                verticesCount += geometry.attributes.position.count;
                            }
                        }
                    } else {
                        trianglesCount += geometry.attributes.position.count / 3;
                        
                        // For non-indexed geometries with large vertex counts
                        const positionCount = geometry.attributes.position.count;
                        
                        if (positionCount > 500000) { // If more than 500k vertices, use sampling
                            const positions = geometry.attributes.position.array;
                            const sampleRate = Math.max(1, Math.floor(positionCount / 50000)); // Sample ~50k vertices
                            const sampleMap = new Map();
                            
                            // Sample vertices to estimate uniqueness
                            for (let i = 0; i < positions.length; i += (3 * sampleRate)) {
                                if (i + 2 < positions.length) {
                                    const vertexKey = `${positions[i].toFixed(3)},${positions[i+1].toFixed(3)},${positions[i+2].toFixed(3)}`;
                                    sampleMap.set(vertexKey, true);
                                }
                            }
                            
                            // Estimate total unique vertices based on sample
                            const uniqueInSample = sampleMap.size;
                            const estimatedUnique = Math.round(uniqueInSample * sampleRate);
                            verticesCount += Math.min(estimatedUnique, positionCount);
                            
                            // console.log("Large non-indexed geometry - estimated unique vertices:", estimatedUnique);
                        } else {
                            // For smaller models, use the original approach with reduced precision
                            try {
                                const positions = geometry.attributes.position.array;
                                const uniqueVertexMap = new Map();
                                
                                // Check every vertex position (x,y,z triplet) with reduced precision
                                for (let i = 0; i < positions.length; i += 3) {
                                    const vertexKey = `${positions[i].toFixed(3)},${positions[i+1].toFixed(3)},${positions[i+2].toFixed(3)}`;
                                    uniqueVertexMap.set(vertexKey, true);
                                }
                                
                                const uniqueVertices = uniqueVertexMap.size;
                                verticesCount += uniqueVertices;
                                
                                // console.log("Non-indexed geometry - estimated unique vertices:", uniqueVertices);
                            } catch (e) {
                                // Fallback if Map creation fails
                                console.warn("Map creation failed, using position count as estimate");
                                verticesCount += positionCount;
                            }
                        }
                    }
                }
            }
        });
        
        // console.log("Final counts:", {vertices: verticesCount, triangles: trianglesCount, parts: partsCount});
        
        return {
            vertices: Math.round(verticesCount),
            triangles: Math.round(trianglesCount),
            parts: partsCount
        };
    };
    // Helper to check if both models are loaded.
    checkModelsLoaded = () => {
        if (this.leftModelLoaded && this.rightModelLoaded) {
            this.setState({ webGLLoading: false });
        }
    };
    componentDidMount() {
        this._isMounted = true;
        
        // Add global error handler for THREE.js errors that might occur during unmounting
        this.originalConsoleError = console.error;
        console.error = (...args) => {
            // Filter out known THREE.js errors that occur during cleanup
            if (args[0] && typeof args[0] === 'string' && 
                (args[0].includes('THREE') || args[0].includes('Cannot read properties of null'))) {
                // Only log these if component is still mounted
                if (this._isMounted) {
                    this.originalConsoleError.apply(console, args);
                }
            } else {
                this.originalConsoleError.apply(console, args);
            }
        };
        
        this.initThreeJSViewer();
        window.addEventListener('resize', this.handleResize);
    }
    
    componentWillUnmount() {
        this._isMounted = false;
    
        // Restore original console.error
        if (this.originalConsoleError) {
            console.error = this.originalConsoleError;
        }
        window.removeEventListener('resize', this.handleResize);
        
        // Cancel all animations
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        
        // Stop all continuous operations
        this.zooming = false;
        this.turning = false;
        this.shifting = false;
        
        // Cleanup THREE.js resources
        if (this.renderer) {
            this.renderer.dispose();
            this.renderer.forceContextLoss();
            this.renderer.domElement.remove();
            this.renderer = null;
        }
        
        // Clear any other references
        this.sceneLeft = null;
        this.sceneRight = null;
        this.cameraLeft = null;
        this.cameraRight = null;
        this.controls = null;
        this.preloadedRightModels = {};
        this.currentRightObject = null;
    }
    // fetchDecimationMetrics = () => {
    //     const hostname = window.location.hostname;
    //     const { modelId } = this.props;
    //     const url = `http://${hostname}:5000/api/mp/decimate/${modelId}/${modelId}.json`;
    //     fetch(url)
    //         .then(response => response.json())
    //         .then(data => {
    //             this.setState({ metrics: data });
    //         })
    //         .catch(error => {
    //             console.error("Error fetching decimation metrics:", error);
    //         });
    // };
    formatFileSize = (sizeInBytes) => {
        if (!sizeInBytes) return "0 KB";
        
        const sizeInKB = sizeInBytes / 1024;
        if (sizeInKB < 1024) {
            return `${Math.round(sizeInKB)} KB`;
        } else {
            return `${(sizeInKB / 1024).toFixed(2)} MB`;
        }
    };
    // Add this helper method to create grids
    addGridToScene = (scene, size = 100, divisions = 100) => {
        // Create a grid helper
        const grid = new THREE.GridHelper(size, divisions, 0x888888, 0xcccccc);
        
        // Position it slightly below the expected "ground level" to avoid z-fighting
        grid.position.y = -0.01;
        
        // Add it to the scene
        scene.add(grid);
        
        return grid;
    };

    normalizeObject = (object) => {
        const targetSize = 10; // All models will be scaled to fit within a 10x10x10 cube.

        // 1. Scale the model to a uniform size.
        const initialBox = new THREE.Box3().setFromObject(object);
        const initialSize = initialBox.getSize(new THREE.Vector3());
        const maxDim = Math.max(initialSize.x, initialSize.y, initialSize.z);
        if (maxDim > 0) {
            const scaleFactor = targetSize / maxDim;
            object.scale.set(scaleFactor, scaleFactor, scaleFactor);
        }

        // 2. Center the scaled model at the origin.
        const scaledBox = new THREE.Box3().setFromObject(object);
        const center = scaledBox.getCenter(new THREE.Vector3());
        object.position.sub(center);

        // 3. Place the model on top of the grid (y=0).
        const finalBox = new THREE.Box3().setFromObject(object);
        object.position.y -= finalBox.min.y;
    }

    frameObject = (object, camera, controls) => {
        const padding = 1.5; // Controls how "zoomed out" the camera is. 1.0 is a tight fit.

        // First, normalize the object's scale and position.
        this.normalizeObject(object);
        
        // Get the model's new size and center point for camera framing.
        const box = new THREE.Box3().setFromObject(object);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);

        // Calculate the camera distance required to frame the object.
        const fov = camera.fov * (Math.PI / 180);
        const cameraDistance = Math.abs(maxDim / Math.tan(fov / 2)) / 2;

        // Position the camera to achieve a consistent 3/4 viewing angle.
        camera.position.set(
            center.x + cameraDistance,
            center.y + cameraDistance * 0.5,
            center.z + cameraDistance
        ).multiplyScalar(padding);

        // Point the camera at the center of the model.
        controls.target.copy(center);
        controls.update();
    };

    standardizeMaterials = (object) => {
        object.traverse(child => {
            if (child.isMesh) {
                // Create a standard, non-reflective white material
                const standardMaterial = new THREE.MeshPhongMaterial({ color: 0xffffff });
                child.material = standardMaterial;
            }
        });
    };

    initThreeJSViewer = () => {
        const container = this.containerRef.current;
        if (!container) return;
        const width = container.clientWidth;
        const height = container.clientHeight;
        
        // Create one renderer attached to the container
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        container.appendChild(this.renderer.domElement);
        
        // Create two cameras (left and right)
        this.cameraLeft = new THREE.PerspectiveCamera(30, width / height, 0.1, 1000);
        this.cameraLeft.position.set(0, 5, 10);

        this.cameraRight = new THREE.PerspectiveCamera(30, width / height, 0.1, 1000);
        this.cameraRight.position.set(0, 5, 10);
        
        // Setup OrbitControls on the master camera (left)
        this.controls = new OrbitControls(this.cameraLeft, this.renderer.domElement);
        this.controls.enableDamping = true;
        
        // Create left and right scenes
        this.sceneLeft = new THREE.Scene();
        this.sceneLeft.background = new THREE.Color(0xf0f0f0);
        this.sceneRight = new THREE.Scene();
        this.sceneRight.background = new THREE.Color(0xf0f0f0);
        
        // Add lighting to both scenes
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(1, 1, 1);
        this.sceneLeft.add(ambientLight.clone());
        this.sceneLeft.add(directionalLight.clone());
        this.sceneRight.add(ambientLight.clone());
        this.sceneRight.add(directionalLight.clone());
        
        // Add grid to both scenes
        this.gridLeft = this.addGridToScene(this.sceneLeft);
        this.gridRight = this.addGridToScene(this.sceneRight);
        
        // Create a LoadingManager to track progress
        const manager = new THREE.LoadingManager();
        let leftModelSize = 0;
        manager.onProgress = (url, loaded, total) => {
            const progression = loaded / total;
            console.log('Progress:', progression);
            this.setState({ progression });
        };
        manager.onLoad = () => {
            console.log("All assets loaded");
        };
        manager.onError = (url) => {
            console.error(`There was an error loading ${url}`);
            this.setState({ hasError: true, webGLLoading: false });
        };
        
        // Instantiate GLTFLoader using the manager for the left model
        const loader = new GLTFLoader(manager);
        const hostname = window.location.hostname;
        const { modelId } = this.props;
        const leftUrl = `${window.config.getApiBaseUrl()}/api/mp/decimate/${modelId}/${modelId}_orig.glb`;
        
        // In the left model loader callback, compute and store stats:
        loader.load(
            leftUrl,
            (gltf) => {
                if (!this._isMounted || !this.sceneLeft) return;
                
                const object = gltf.scene;
                this.standardizeMaterials(object);
                this.sceneLeft.add(object);

                // Frame the object and position the camera
                this.frameObject(object, this.cameraLeft, this.controls);
                
                this.leftModelLoaded = true;
                const stats = this.getObjectStats(object);
                if (this._isMounted) {
                    this.setState(prevState => ({
                        metrics: { ...prevState.metrics, original: { ...stats, fileSize: leftModelSize } }
                    }));
                }
                this.checkModelsLoaded();
            },
            (xhr) => {
                // Store the total size when it becomes available
                if (xhr.lengthComputable && xhr.total > 0) {
                    leftModelSize = xhr.total;
                }
            },
            (error) => {
                if (!this._isMounted) return; // Check if still mounted
                
                console.error("Error loading left model", error);
                this.setState({ hasError: true, webGLLoading: false });
            }
        );
        
        // Remove old right model loader.
        this.loadAllRightModels();
        
        this.animate();
        this.webGLLoading = true;
        this.state.modelsLoaded = true;
    }
    // Update loadAllRightModels method
    loadAllRightModels = () => {
        const { modelId } = this.props;
        const hostname = window.location.hostname;
        const decimationValues = [0, 1, 2, 3, 4];
        let loadedCount = 0;
        const totalModels = decimationValues.length;
        const fileSizes = {}; // Object to store file sizes by decimation setting
        
        decimationValues.forEach((setting) => {
            const suffix = (setting + 1).toString();
            const url = `${window.config.getApiBaseUrl()}/api/mp/decimate/${modelId}/${modelId}_${suffix}.glb`;
            console.log(`right url ${url}`);
            
            // Create a loader with onProgress handler to capture file size
            const loader = new GLTFLoader();
            loader.load(
                url,
                (gltf) => {
                    if (!this._isMounted) return;
                    
                    const object = gltf.scene;
                    this.standardizeMaterials(object);
                    
                    // Normalize the object's scale and position to be uniform
                    this.normalizeObject(object);
                    
                    // Get stats for the model
                    const stats = this.getObjectStats(object);
                    
                    // Save the preloaded object with its stats and file size
                    this.preloadedRightModels[setting] = {
                        object: object,
                        stats: { ...stats, fileSize: fileSizes[setting] || 0 }
                    };
                    
                    loadedCount++;
                    if (loadedCount === totalModels && this._isMounted) {
                        // After preloading, update the right model to match the current slider value
                        this.updateRightModel();
                        this.rightModelLoaded = true;
                        this.checkModelsLoaded();
                    }
                },
                (xhr) => {
                    // Store file size during loading
                    if (xhr.lengthComputable && xhr.total > 0) {
                        fileSizes[setting] = xhr.total;
                    }
                },
                (error) => {
                    if (!this._isMounted) return;
                    console.error(`Error loading right model for decimation ${setting}:`, error);
                }
            );
        });
    };

    // Also update the updateRightModel method
    updateRightModel = () => {
        if (!this._isMounted || !this.sceneRight) return;
        
        if (this.currentRightObject) {
            this.sceneRight.remove(this.currentRightObject);
        }
        
        const currentSetting = this.state.decimationSetting;
        const modelData = this.preloadedRightModels[currentSetting];
        
        if (modelData && modelData.object) {
            // Clone the object so that future swaps aren't affected
            const newObject = modelData.object.clone();
            this.sceneRight.add(newObject);
            this.currentRightObject = newObject;
            
            // The grid no longer needs to be moved as models are pre-positioned
            
            // Update metrics with the stored stats including file size
            const decimationKey = this.getDecimationKey();
            if(this._isMounted) {
                this.setState(prevState => ({
                    metrics: { ...prevState.metrics, [decimationKey]: modelData.stats }
                }));
            }
        } else {
            console.warn("No preloaded model found for decimation setting:", currentSetting);
        }
    };




    animate = () => {
        if (!this._isMounted) return;
        
        this.controls.update();
    
        const canvas = this.renderer.domElement;
        const fullWidth = canvas.clientWidth;
        const fullHeight = canvas.clientHeight;
        const halfWidth = fullWidth / 2;
        
        // Update left camera—set its aspect to half the width:
        this.cameraLeft.aspect = halfWidth / fullHeight;
        this.cameraLeft.updateProjectionMatrix();
        
        // Update right camera by copying the left camera's parameters:
        this.cameraRight.copy(this.cameraLeft);
        this.cameraRight.updateProjectionMatrix();
        
        // Render left scene:
        this.renderer.setViewport(0, 0, halfWidth, fullHeight);
        this.renderer.setScissor(0, 0, halfWidth, fullHeight);
        this.renderer.setScissorTest(true);
        this.renderer.render(this.sceneLeft, this.cameraLeft);
        
        // Render right scene:
        this.renderer.setViewport(halfWidth, 0, halfWidth, fullHeight);
        this.renderer.setScissor(halfWidth, 0, halfWidth, fullHeight);
        this.renderer.render(this.sceneRight, this.cameraRight);
        
        // Store animation frame ID so we can cancel it later
        this.animationFrameId = requestAnimationFrame(this.animate);
    }
    
    handleResize = () => {
        if (!this.containerRef.current) return;
        const width = this.containerRef.current.clientWidth;
        const height = this.containerRef.current.clientHeight;
        this.renderer.setSize(width, height);
        // Update both cameras
        this.cameraLeft.aspect = width/height;
        this.cameraLeft.updateProjectionMatrix();
        this.cameraRight.aspect = width/height;
        this.cameraRight.updateProjectionMatrix();
    }
    
    // Update the decimation value then load the new right model.
    setDecimation = (decimationSetting) => {
        this.setState({ decimationSetting }, () => {
            this.updateRightModel();
        });
    };
    
    toggleWebGLSettings = (state) => {
        this.setState(prevState => ({ displayWebGLSettings: state === false ? state : !prevState.displayWebGLSettings }));
    };

    renderDecimationText = () => {
        switch (this.state.decimationSetting) {
            case 0:
                return 'Lowest';
            case 1:
                return 'Low';
            case 3:
                return 'High';
            case 4:
                return 'Highest';
            default:
                return 'Medium';
        }
    };

    getDecimationKey = () => {
        switch (this.state.decimationSetting) {
            case 0:
                return 'very_low';
            case 1:
                return 'low';
            case 3:
                return 'high';
            case 4:
                return 'very_high';
            default:
                return 'medium';
        }
    };


    // Continuous shifting helpers

    startContinuousShift = (dirX, dirY) => {
        this.shifting = true;
        this.lastShiftTime = null;
        const shiftAnimate = (time) => {
            if (!this._isMounted || !this.shifting) {
                this.lastShiftTime = null;
                return;
            }
            
            if (this.lastShiftTime === null) {
                this.lastShiftTime = time;
            }
            
            const deltaTime = time - this.lastShiftTime;
            const speed = 0.003; // adjust as needed
            const distance = speed * deltaTime;
    
            // Get the camera's current forward vector
            const forward = new THREE.Vector3();
            this.cameraLeft.getWorldDirection(forward);
    
            // Compute the right vector from forward and up (assumes no camera roll)
            const right = new THREE.Vector3();
            right.crossVectors(forward, this.cameraLeft.up).normalize();
    
            // Use the camera's up vector directly
            const up = this.cameraLeft.up.clone().normalize();
    
            // Calculate the shift vector based on the provided directions
            const shift = new THREE.Vector3();
            shift.addScaledVector(right, dirX * distance);
            shift.addScaledVector(up, dirY * distance);
    
            // Apply the shift to both the camera position and the control target
            this.cameraLeft.position.add(shift);
            this.controls.target.add(shift);
    
            this.lastShiftTime = time;
            this.shiftFrameId = requestAnimationFrame(shiftAnimate);
        };
        
        this.shiftFrameId = requestAnimationFrame(shiftAnimate);
    };
    
    stopContinuousShift = () => {
        this.shifting = false;
        if (this.shiftFrameId) {
            cancelAnimationFrame(this.shiftFrameId);
            this.shiftFrameId = null;
        }
    }

    // Replace existing onShift methods with the following continuous versions:
    onShiftUp = () => { this.startContinuousShift(0, 1); }
    onShiftDown = () => { this.startContinuousShift(0, -1); }
    onShiftLeft = () => { this.startContinuousShift(-1, 0); }
    onShiftRight = () => { this.startContinuousShift(1, 0); }
    onShiftStop = () => { this.stopContinuousShift(); }








    // Add these helper methods inside your DecimationReviewForm class (for example after toggleWebGLSettings)

    rotateLeft = (angle) => {
        // rotate horizontally about the world up axis (0,1,0)
        const axis = new THREE.Vector3(0, 1, 0);
        // Get offset from target to camera
        const offset = new THREE.Vector3().subVectors(this.cameraLeft.position, this.controls.target);
        offset.applyAxisAngle(axis, angle);
        this.cameraLeft.position.copy(this.controls.target).add(offset);
        this.cameraLeft.lookAt(this.controls.target);
    };

    rotateUp = (angle) => {
        // rotate vertically about the camera's right vector
        const right = new THREE.Vector3();
        right.crossVectors(this.cameraLeft.getWorldDirection(new THREE.Vector3()), this.cameraLeft.up).normalize();
        const offset = new THREE.Vector3().subVectors(this.cameraLeft.position, this.controls.target);
        offset.applyAxisAngle(right, angle);
        this.cameraLeft.position.copy(this.controls.target).add(offset);
        this.cameraLeft.lookAt(this.controls.target);
    };






    startContinuousTurn = (direction) => {
        this.turning = true;
        this.lastTurnTime = null;
        const turnAnimate = (time) => {
            if (!this._isMounted || !this.turning) {
                this.lastTurnTime = null;
                return;
            }
            
            if (this.lastTurnTime === null) {
                this.lastTurnTime = time;
            }
            
            const deltaTime = time - this.lastTurnTime;
            const angularSpeed = 0.001; // radians per ms
            const angle = angularSpeed * deltaTime;
            
            switch(direction) {
                case 'up':
                    this.rotateUp(-angle);  // reversed
                    break;
                case 'down':
                    this.rotateUp(angle);   // reversed
                    break;
                case 'left':
                    this.rotateLeft(-angle); // reversed
                    break;
                case 'right':
                    this.rotateLeft(angle); // reversed
                    break;
                default:
                    break;
            }
            
            this.lastTurnTime = time;
            this.turnFrameId = requestAnimationFrame(turnAnimate);
        };
        
        this.turnFrameId = requestAnimationFrame(turnAnimate);
    };
    
    stopContinuousTurn = () => {
        this.turning = false;
        if (this.turnFrameId) {
            cancelAnimationFrame(this.turnFrameId);
            this.turnFrameId = null;
        }
    };
    // Replace existing onTurn methods (which previously logged clicks) with continuous turning versions:
    onTurnUp = () => { this.startContinuousTurn('up'); }
    onTurnDown = () => { this.startContinuousTurn('down'); }
    onTurnLeft = () => { this.startContinuousTurn('left'); }
    onTurnRight = () => { this.startContinuousTurn('right'); };
    
    // And create a stop handler for the turning buttons:
    onTurnStop = () => { this.stopContinuousTurn(); };




    startContinuousZoom = (direction) => {
        this.zooming = true;
        this.lastZoomTime = null;
        
        const zoomAnimate = (time) => {
            if (!this._isMounted || !this.zooming) {
                this.lastZoomTime = null;
                return;
            }
            
            if (this.lastZoomTime === null) {
                this.lastZoomTime = time;
            }
            
            const deltaTime = time - this.lastZoomTime;
            
            // Get current distance to target
            const distanceToTarget = this.cameraLeft.position.distanceTo(this.controls.target);
            
            // Dynamic speed based on distance (faster when farther away)
            const baseSpeed = 0.003;
            const zoomSpeed = baseSpeed * Math.max(1, distanceToTarget / 10);
            
            const move = zoomSpeed * deltaTime;
            
            // Compute unit vector from camera to target
            const vec = new THREE.Vector3();
            vec.subVectors(this.controls.target, this.cameraLeft.position).normalize();
            
            // Apply zoom with limits
            const minDistance = 0.5;  // Minimum distance to prevent going inside model
            const maxDistance = 50;   // Maximum zoom out distance
            
            if (direction === 'in' && distanceToTarget > minDistance) {
                this.cameraLeft.position.addScaledVector(vec, move);
            } else if (direction === 'out' && distanceToTarget < maxDistance) {
                this.cameraLeft.position.addScaledVector(vec, -move);
            }
            
            this.cameraLeft.lookAt(this.controls.target);
            
            this.lastZoomTime = time;
            this.zoomFrameId = requestAnimationFrame(zoomAnimate);
        };
        
        this.zoomFrameId = requestAnimationFrame(zoomAnimate);
    };
    
    stopContinuousZoom = () => {
        this.zooming = false;
        if (this.zoomFrameId) {
            cancelAnimationFrame(this.zoomFrameId);
            this.zoomFrameId = null;
        }
    };
    
    // Continuous zoom button handlers
    onZoomInDown = () => { this.startContinuousZoom('in'); };
    onZoomOutDown = () => { this.startContinuousZoom('out'); };
    onZoomStop = () => { this.stopContinuousZoom(); };





    renderModel() {
        const { metrics } = this.state;
        // Provide safer defaults with all required properties
        const original = metrics && metrics.original ? metrics.original : { triangles: 0, vertices: 0, parts: 0, fileSize: 0 };
        const decimationKey = this.getDecimationKey();
        const decimated = metrics && metrics[decimationKey] ? metrics[decimationKey] : { triangles: 0, vertices: 0, parts: 0, fileSize: 0 };
    
        return (
            <div className="flex-centered" style={{ position: "relative", height: "100%" }}>
                <div
                    id="threejs-window"
                    className="decimated-models flex-centered max-width"
                    style={{ width: '100%', height: '100%', position: 'relative' }}
                    ref={this.containerRef}
                >
                    {/* Vertical separator line */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        height: '100%',
                        width: '5px',
                        backgroundColor: '#081F5F',
                        zIndex: 10,
                        transform: 'translateX(-0.5px)',
                        pointerEvents: 'none',
                        boxShadow: '0 0 2px rgba(8, 31, 95, 0.5)'
                    }} />
                    {/* Overlay for left scene */}
                    <div style={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '10px',
                        background: 'rgba(255,255,255,0.7)',
                        padding: '5px',
                        border: '1px solid #ccc',
                        fontSize: '20px',
                        zIndex: 20
                    }}>
                        <div>Poly Count: {original.triangles}</div>
                        <div>Vert Count: {original.vertices}</div>
                        <div>Part Count: {original.parts}</div>
                        <div>File Size: {this.formatFileSize(original.fileSize)}</div>
                    </div>
                    {/* Overlay for right scene */}
                    <div style={{
                        position: 'absolute',
                        bottom: '10px',
                        left: 'calc(50% + 10px)',
                        background: 'rgba(255,255,255,0.7)',
                        padding: '5px',
                        border: '1px solid #ccc',
                        fontSize: '20px',
                        zIndex: 20
                    }}>
                        <div>Poly Count: {decimated.triangles}</div>
                        <div>Vert Count: {decimated.vertices}</div>
                        <div>Part Count: {decimated.parts}</div>
                        <div>File Size: {this.formatFileSize(decimated.fileSize)}</div>
                    </div>
                </div>
                <ModelControls 
                    onShiftUp={this.onShiftUp}
                    onShiftDown={this.onShiftDown}
                    onShiftLeft={this.onShiftLeft}
                    onShiftRight={this.onShiftRight}
                    onShiftStop={this.onShiftStop} 
                    onTurnUp={this.onTurnUp}
                    onTurnDown={this.onTurnDown}
                    onTurnLeft={this.onTurnLeft}
                    onTurnRight={this.onTurnRight}
                    onTurnStop={this.onTurnStop}
                    onZoomInDown={this.onZoomInDown}
                    onZoomOutDown={this.onZoomOutDown}
                    onZoomStop={this.onZoomStop}
                />
            </div>
        );
    }
    render() {
        return (
            <Fragment>
                {this.state.webGLLoading 
                && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999,
                            backgroundColor: 'rgba(255,255,255,0.5)', // optional overlay color
                        }}
                    >
                        <CircularProgress size={50} variant="indeterminate" />
                    </div>
                )}
                <div id="file-review-container" className="flex-apart">
                    {this.state.promptingDelete && 
                        <Dialogue
                            alert={"Would you like to discard this model?"}
                            confirmAction={() => this.props.removeModel(this.props.modelId)}
                            cancel={() => this.setState({ promptingDelete: false })}
                        />
                    }
                    {this.state.promptingApprove && 
                        <Dialogue
                            alert={"Would you like to approve/save the decimated model?"}
                            confirmAction={() => this.props.approveModel(this.props.modelId, this.state.decimationSetting, this.props.modelName, this.state.backgroundColor)}
                            cancel={() => this.setState({ promptingApprove: false })}
                        />
                    }
                    <div id="file-review-header-container" className="flex">
                        <p id="file-review-header">Filename:</p>
                        <p style={{ marginLeft: '.25em' }}>{this.props.modelName}</p>
                    </div>
                    {this.state.modelsLoaded && (
                        <div id="decimation-review-actions-container" className="flex-apart">
                            {!this.state.hasError && (
                                <div id="file-review-header-container" className="flex-column">
                                    <p id="file-review-header">{`Decimation Settings: ${this.renderDecimationText()}`}</p>
                                    <DecimationSlider setDecimation={this.setDecimation} />
                                </div>
                            )}
                            <div id="file-review-header-container" className="flex-apart" style={{ width: '20%' }}>
                                {!this.state.hasError && approveIcon(() => this.setState({ promptingApprove: true }))}
                                {deleteIcon(() => this.setState({ promptingDelete: true }))}
                                {this.state.hasError && (
                                    <span style={{ color: 'red', marginLeft: '1em', alignSelf: 'center' }}>Error loading models.</span>
                                )}
                            </div>
                            <div id="file-review-header-container" className="clickable flex" onClick={this.props.closeModal}>
                                <p style={{ marginRight: '.25em', justifySelf: 'flex-end' }}>Close Window</p>
                                <div>{circleX("#081F5F")}</div>
                            </div>
                        </div>
                    )}
                </div>
                <div id="decimated-models-view-window" style={{ height:"calc(100% - 103px)" }}>
                    {this.renderModel()}
                </div>
            </Fragment>
        );
    }
}

export default DecimationReviewForm;